import React, { useState, useEffect } from 'react';
import { X, Wrench } from 'lucide-react';
import api from '../../utils/api';

/**
 * ServiceCallModal
 * Props:
 *   customerId  - required when creating
 *   call        - existing service call object (edit mode)
 *   onClose     - fn()
 *   onSaved     - fn(savedCall)
 */
export default function ServiceCallModal({ customerId, call, onClose, onSaved }) {
  const isEdit = !!call;
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState(call?.type_id || '');
  const [details, setDetails] = useState(call?.details || '');
  const [status, setStatus] = useState(call?.status || 'open');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/service-call-types').then((r) => setTypes(r.data)).catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let res;
      if (isEdit) {
        res = await api.put(`/service-calls/${call.id}`, {
          type_id: typeId || null,
          details: details || null,
          status,
        });
      } else {
        res = await api.post('/service-calls', {
          customer_id: customerId,
          type_id: typeId || null,
          details: details || null,
        });
      }
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Wrench size={18} className="text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEdit ? 'עריכת קריאת שירות' : 'קריאת שירות חדשה'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Type select */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">סוג קריאה</label>
            <select
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">— בחר סוג —</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* Details */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">פירוט</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={4}
              placeholder="תאר את הבעיה או הבקשה..."
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          {/* Status — edit mode only */}
          {isEdit && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">סטטוס</label>
              <div className="flex gap-3">
                {[{ val: 'open', label: 'פתוחה' }, { val: 'closed', label: 'סגורה' }].map(({ val, label }) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setStatus(val)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      status === val
                        ? val === 'open'
                          ? 'bg-green-100 border-green-400 text-green-800 dark:bg-green-900/30 dark:border-green-600 dark:text-green-300'
                          : 'bg-gray-100 border-gray-400 text-gray-700 dark:bg-gray-700 dark:border-gray-500 dark:text-gray-300'
                        : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              ביטול
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60 transition-colors"
            >
              {saving ? 'שומר...' : isEdit ? 'שמור שינויים' : 'צור קריאה'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
